/**
 * One roster row, and its children when it is open (BON-534).
 *
 * Recursive, so the roster works at whatever depth the response carries.
 */
import { useState } from 'react';

import type { NestedRow } from '../../primitives/scope/nest-rows';

import type { OverviewRegionRow } from './overview-types';
import { RegionRosterName } from './RegionRosterName';
import { ROSTER_CELL, rosterCellContent } from './roster-cell';
import { gridTemplateFor, ROSTER_COLUMNS } from './roster-columns';

export interface RegionRosterRowProps {
  node: NestedRow<OverviewRegionRow>;
  /** Indent relative to the roster's own top level, not the hierarchy's, so a
   *  region page's roster does not indent everything by that region's depth. */
  indent: number;
}

export function RegionRosterRow({ node, indent }: RegionRosterRowProps) {
  const [open, setOpen] = useState(false);
  const { row } = node;
  // Driven by what this response actually carries, not by `hasChildren`, so the
  // caret never promises rows the roster cannot show at the requested depth.
  const expandable = node.children.length > 0;

  const name = (
    <RegionRosterName
      row={row}
      indent={indent}
      expandable={expandable}
      open={open}
      onToggle={() => setOpen((was) => !was)}
      subCount={node.children.length}
    />
  );

  return (
    <>
      <div
        role="row"
        data-testid={`roster-row-${row.id}`}
        style={{
          display: 'grid',
          gridTemplateColumns: gridTemplateFor(ROSTER_COLUMNS),
          alignItems: 'center',
          minHeight: 40,
          borderTop: '1px solid var(--nb-border)',
          background: indent > 0 ? 'var(--nb-surface)' : 'transparent',
        }}
      >
        {ROSTER_COLUMNS.map((column) => (
          <span
            key={column.id}
            role="cell"
            className={column.align === 'right' ? 'nb-tabular nb-mono' : undefined}
            style={{
              ...ROSTER_CELL,
              justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start',
              ...(column.muted ? { color: 'var(--nb-muted)', fontWeight: 400 } : null),
            }}
          >
            {rosterCellContent(column, row, name)}
          </span>
        ))}
      </div>
      {open &&
        node.children.map((child) => (
          <RegionRosterRow key={child.row.id} node={child} indent={indent + 1} />
        ))}
    </>
  );
}
